import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {BoxProps, Flex, HStack, Skeleton, useMultiStyleConfig, VStack} from '@chakra-ui/react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useIntl} from 'react-intl'
import {useCustomApi, useAnalytics} from '_customHooks'
import {Button, Tabs} from '_uiComponents'
import {capitalize} from '_utils'
import {SizeGuidePdp, MeasurementsSection, MeasurementsItem, PageDesignerMeasurementsAsset, PageDesignerMeasurementsSubSection} from '_types'
import SizeGuideTab, {SizeGuideTabSkeleton} from './partials/size-guide-tab'
import MeasurementsTab from './partials/measurements-tab'

interface SizeGuideProps {
    sizeChartID: string
    pageDesignerSizeGuideContent: PageDesignerMeasurementsAsset[]
    openSizeSelector?: () => void
    openHelpWithSizes?: () => void
}

const SizeGuide = ({sizeChartID, pageDesignerSizeGuideContent, openSizeSelector, openHelpWithSizes}: SizeGuideProps) => {
    const {formatMessage} = useIntl()
    const {locale} = useMultiSite()
    const {useSizeChart} = useCustomApi()
    const {analyticsEventPdpSizeGuideLayer} = useAnalytics()
    const {mutateAsync: getSizeChartMutation} = useSizeChart()
    const [sizeGuidePdp, setSizeGuidePdp] = useState<SizeGuidePdp>()
    const [isLoading, setIsLoading] = useState(true)

    // Owns the full `pdp_size_guide_layer` lifecycle (open/switch/close) so the event fires consistently
    // regardless of how the layer is dismissed: back arrow, the bottom "Size Selector" CTA, overlay/ESC.
    // These paths don't all route through the drawer's onClose (drawer-to-drawer transitions overwrite the
    // close callback without invoking it), so tracking is centralized here rather than in the PDP.
    // `activeTabNameRef` keeps the last active tab for the close payload; `hasClosedRef` guards against
    // emitting close twice (e.g. an explicit close followed by the unmount cleanup).
    const activeTabNameRef = useRef<string>('')
    const hasClosedRef = useRef<boolean>(false)

    // Fixed English analytics name for a size-guide tab by position, decoupled from the localized/dynamic
    // tab labels so the analytics value is stable regardless of the user's language (manuale BitBang PDP
    // PWA §3.1.3). Index 0 is the Size Guide; every following tab is a per-fit "Measurements" tab, suffixed
    // with its 1-based position when more than one exists, to keep them distinguishable.
    const getTabAnalyticsName = useCallback((index: number, totalTabs: number): string => {
        if (index <= 0) return 'Size guide'
        const measurementsCount = totalTabs - 1
        return measurementsCount > 1 ? `Measurements ${index}` : 'Measurements'
    }, [])

    const emitClose = useCallback(() => {
        if (hasClosedRef.current) return
        hasClosedRef.current = true
        analyticsEventPdpSizeGuideLayer({tabName: activeTabNameRef.current, action: 'close'})
    }, [analyticsEventPdpSizeGuideLayer])

    // Wraps the "Size Selector" CTA (bottom button and Size Guide tab) so the layer close is emitted
    // before navigating to the size selector, since that transition replaces the drawer without firing
    // the drawer's own close callback. Undefined when no size-selector navigation is available.
    const handleOpenSizeSelector = useCallback(() => {
        if (!openSizeSelector) return
        emitClose()
        openSizeSelector()
    }, [openSizeSelector, emitClose])

    const styles = useMultiStyleConfig('SizeGuide', {}) as Record<string, BoxProps>

    const LABEL_SIZE_SELECTOR = formatMessage({id: 'label.pdp.select.size', defaultMessage: 'Select size'})
    const LABEL_SIZES_HELP = formatMessage({id: 'label.pdp.need.help', defaultMessage: 'Need help?'})

    // Fetch once when the drawer opens, regardless of prop changes during its lifetime
    useEffect(() => {
        getSizeChartMutation({locale: locale.id, sizeChartID})
            .then((response) => {
                setSizeGuidePdp(response.sizeGuide.pdp)
            })
            .catch((error) => {})
            .finally(() => {
                setIsLoading(false)
            })
    }, [])

    // Looks up the measurements asset matching contentId. A regular asset maps to a single MeasurementsSection;
    // a 'textBox' asset groups multiple sub-sections, each of which becomes its own MeasurementsSection/tab.
    const filteredData = (contentId?: string): MeasurementsSection[] => {
        if (!contentId) {
            return []
        }

        const asset = pageDesignerSizeGuideContent?.find((item) => item.name === contentId)

        if (!asset) {
            return []
        }

        const buildItems = (rawItems?: {title: string; description?: string}[]): MeasurementsItem[] =>
            (rawItems ?? [])
                .map((item): MeasurementsItem | undefined => (item.title ? {title: item.title, text: item.description ?? ''} : undefined))
                .filter((item): item is MeasurementsItem => Boolean(item))

        if (asset.type === 'textBox') {
            return (asset.items as PageDesignerMeasurementsSubSection[]).map((subSection) => {
                const items = buildItems(subSection.items)
                return {
                    title: subSection.title ?? '',
                    subtitle: subSection.description ?? '',
                    imageDesktop: subSection.imageDesktop ?? undefined,
                    imageTablet: subSection.imageTablet ?? undefined,
                    imageMobile: subSection.imageMobile ?? undefined,
                    details: items.length ? [{items}] : [],
                }
            })
        }

        const items = buildItems(asset.items as {title: string; description?: string}[])

        return [
            {
                title: asset.title ?? '',
                subtitle: asset.description ?? '',
                imageDesktop: asset.imageDesktop ?? undefined,
                imageTablet: asset.imageTablet ?? undefined,
                imageMobile: asset.imageMobile ?? undefined,
                details: items.length ? [{items}] : [],
            },
        ]
    }

    const tabs = useMemo(() => {
        const allSections = sizeGuidePdp?.sections ?? []
        // Sections carrying only a Page Designer measurements asset (no table data) contribute measurements content
        const assetOnlySections = allSections.filter((section) => section.guideAsset && section.isOnlyAsset)
        const tableSections = allSections.filter((section) => !(section.guideAsset && section.isOnlyAsset))

        const headerItems = sizeGuidePdp?.header ?? []
        // A single header entry (or none) means there's no real "Measurements" tab structure: everything —
        // tables and any asset-only sections, textBox sub-sections included — stays inside the one Size Guide tab
        const hasMeasurementsTabs = headerItems.length > 1

        if (!hasMeasurementsTabs) {
            const assetMeasurementsSections = assetOnlySections.flatMap((section) => filteredData(section.guideAsset))
            return [
                {
                    label: formatMessage({id: 'label.pdp.size.guide', defaultMessage: 'Size Guide'}),
                    content: isLoading ? (
                        <SizeGuideTabSkeleton />
                    ) : (
                        <>
                            <SizeGuideTab sections={tableSections} />
                            {assetMeasurementsSections.map((measurementsSection, index) => (
                                <MeasurementsTab key={index} section={measurementsSection} />
                            ))}
                        </>
                    ),
                },
            ]
        }

        const sizeGuideTab = {
            label: formatMessage({id: 'label.pdp.size.guide', defaultMessage: 'Size Guide'}),
            content: isLoading ? <SizeGuideTabSkeleton /> : <SizeGuideTab sections={tableSections} />,
        }

        // Every header entry past the first is its own "Measurements" tab, one per fit; its `content` is matched
        // against a Page Designer content asset, which may itself expand into several sub-section tabs
        const headerMeasurementsTabs = headerItems.slice(1).flatMap((headerItem) => {
            const measurementsSections = filteredData(headerItem.content)
            if (measurementsSections.length === 0) {
                return [{label: capitalize(headerItem.name), content: isLoading ? <Skeleton height="100px" /> : <MeasurementsTab section={undefined} />}]
            }
            return measurementsSections.map((measurementsSection) => ({
                label: capitalize(headerItem.name),
                content: isLoading ? <Skeleton height="100px" /> : <MeasurementsTab section={measurementsSection} />,
            }))
        })

        const assetMeasurementsTabs = assetOnlySections.flatMap((section) => { 
            const measurementsSections = filteredData(section.guideAsset)
            if (measurementsSections.length === 0) {
                return [{label: section.name, content: isLoading ? <Skeleton height="100px" /> : <MeasurementsTab section={undefined} />}]
            }
            return measurementsSections.map((measurementsSection) => ({
                label: measurementsSection.title,
                content: isLoading ? <Skeleton height="100px" /> : <MeasurementsTab section={measurementsSection} />,
            }))
        })

        return [sizeGuideTab, ...headerMeasurementsTabs, ...assetMeasurementsTabs]
    }, [sizeGuidePdp, isLoading, handleOpenSizeSelector])

    // Once the tabs are resolved from the size-chart data, emit the `pdp_size_guide_layer` open event
    // (manuale BitBang PDP PWA §3.1.3). Fires once per load; seeds the active tab for later switch/close.
    useEffect(() => {
        if (isLoading) return
        const initialTabName = getTabAnalyticsName(0, tabs.length)
        activeTabNameRef.current = initialTabName
        analyticsEventPdpSizeGuideLayer({tabName: initialTabName, action: 'open'})
    }, [isLoading])

    // Fallback close on unmount: covers dismissals that don't go through a wrapped CTA (overlay, ESC,
    // back arrow, product/color change). No-op if a close was already emitted explicitly.
    useEffect(() => {
        return () => emitClose()
    }, [emitClose])

    const handleTabChange = (index: number) => {
        const tabName = getTabAnalyticsName(index, tabs.length)
        activeTabNameRef.current = tabName
        analyticsEventPdpSizeGuideLayer({tabName, action: 'switch'})
    }



    return (
        <VStack {...styles.container} data-testid="size-guide">
            <Flex {...styles.tabsContainer}>
                <Tabs tabs={tabs} variant="sizeGuide" activeIndex={0} onTabChange={handleTabChange} enableDefaultPanels></Tabs>
            </Flex>
            <HStack {...styles.helpCtasContainer}>
                <Button onClick={handleOpenSizeSelector} sx={styles.helpCta} variant="outlineLight">
                    {LABEL_SIZE_SELECTOR}
                </Button>
                <Button sx={{...styles.helpCta, ...styles.helpSizesCta}} variant="outlineLight" onClick={openHelpWithSizes}>
                    {LABEL_SIZES_HELP}
                </Button>
            </HStack>
        </VStack>
    )
}

export default SizeGuide
